import React, { useEffect, useState, useCallback } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useLayout } from "../../contexts/LayoutContext";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import logger from "../../utils/logger";
import { resolveFeatureSections } from "../../utils/featureSections";

// Components
import HeroBanner from "../../components/HeroBanner/HeroBanner";
import ShopByCategory from "./sections/ShopByCategory";
import ProductSlider from "./sections/ProductSlider";
import SectionHeader from "./sections/SectionHeader";
import GetStarted from "./sections/GetStarted/GetStarted"; 
import FeaturesBanner from "../../components/FeaturesBanner/FeaturesBanner";
import LoadingScreen from "../../components/ui/Loading/Loading";


// Icons
import { TrendingUp, ShoppingBag, Loader } from "lucide-react";

import "./Home.css";

export default function Home() {
  const { lang } = useLanguage();
  const { settings } = useLayout();
  const navigate = useNavigate();
  const isAr = lang === "ar";
  const sections = resolveFeatureSections(settings);

  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const normalizeProduct = useCallback((raw) => {
    const name_en = raw.name_en || raw.title || raw.name || "No name";
    const name_ar = raw.name_ar || raw.title_ar || raw.name_en || name_en;
    const originalPrice = Number(
      raw.original_price ?? raw.old_price ?? raw.price ?? 0
    );
    const finalPrice = Number(
      raw.final_price ?? raw.sale_price ?? raw.price ?? 0
    );
    const hasDiscount =
      raw.has_discount === true ||
      (originalPrice > 0 && finalPrice > 0 && finalPrice < originalPrice);

    return {
      ...raw,
      name_en,
      name_ar,
      price: originalPrice > 0 ? originalPrice : 0,
      final_price: finalPrice > 0 ? finalPrice : originalPrice,
      sale_price: hasDiscount ? finalPrice : 0,
      old_price: hasDiscount ? originalPrice : Number(raw.old_price ?? 0),
      original_price: hasDiscount ? originalPrice : 0,
      has_discount: hasDiscount,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!sections.products) {
        if (!cancelled) {
          setFeatured([]);
          setLatest([]);
          setTopSellers([]);
          setBestSellers([]);
          setError("");
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError("");

        const [offersRes, latestRes, topRes] = await Promise.all([
          api.apiGet("products/offers"),
          api.apiGet("products/latest"),
          api.apiGet("products/top"),
        ]);

        if (cancelled) return;

        const offers = (offersRes?.products || []).map(normalizeProduct);
        const latestProducts = (latestRes?.products || []).map(normalizeProduct);
        const topProducts = (topRes?.products || []).map(normalizeProduct);

        setFeatured(offers);
        setLatest(latestProducts);
        setTopSellers(topProducts);

        const best = topProducts
          .filter((p) => p.sales_count > 5 || p.rating > 4)
          .slice(0, 8);
        setBestSellers(best);
      } catch (e) {
        logger.error("Error loading home page products", e);
        if (!cancelled) {
          setError(
            isAr
              ? "حدث خطأ أثناء تحميل بيانات الصفحة."
              : "Error loading home page data.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => (cancelled = true);
  }, [lang, isAr, normalizeProduct, sections.products]);

  // ── Section definitions with icons ──
  const productSections = [
    {
      id: "latest",
      title: isAr ? "وصل حديثًا" : "New Arrivals",
      subtitle: isAr
        ? "اكتشف أحدث المنتجات"
        : "Check out the latest products",
      icon: <ShoppingBag size={26} strokeWidth={2} />,
      products: latest,
      actionButton: {
        text: isAr ? "اكتشف المزيد" : "Discover More",
        onClick: () => navigate("/shop?sort=newest"),
        icon: true,
      },
    },
    {
      id: "top-sellers",
      title: isAr ? "الأكثر مبيعًا" : "Best Sellers",
      subtitle: isAr
        ? "تسوّق المنتجات الأكثر طلبًا"
        : "Shop our most popular items",
      icon: <TrendingUp size={26} strokeWidth={2} />,
      products: topSellers,
      actionButton: {
        text: isAr ? "تسوّق الآن" : "Shop Now",
        onClick: () => navigate("/shop"),
        icon: true,
      },
    },
  ];

  // ── Loading state ──
  if (loading) {
    return (
      <div className="home-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="container py-4">
          <LoadingScreen isAr={isAr} />
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <div className="home-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="container py-4">
          <div className="error-screen">
            <p className="text-danger">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-retry-blue"
            >
              <Loader size={18} />
              <span>{isAr ? "إعادة المحاولة" : "Retry"}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <div className="home-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="container py-4">
        {/* Hero Banner */}
        {sections.products ? (
          <>
            <section className="hero-section">
              <HeroBanner products={featured} />
            </section>

            <ShopByCategory />

            <div className="product-sections-wrapper">
              {productSections.map((section) =>
                section.products.length > 0 ? (
                  <section
                    key={section.id}
                    className={`product-section section-${section.id}`}
                  >
                    <SectionHeader
                      title={section.title}
                      subtitle={section.subtitle}
                      icon={section.icon}
                      actionButton={section.actionButton}
                      align={isAr ? "right" : "left"}
                    />

                    <ProductSlider
                      products={section.products}
                      slidesPerView={{
                        mobile: 1.5,
                        tablet: 2.5,
                        desktop: 3.5,
                        large: 4.5,
                      }}
                      autoplay={false}
                      loop={section.products.length > 4}
                      freeMode={true}
                      showNavigation={section.products.length > 3}
                    />
                  </section>
                ) : null,
              )}
            </div>
          </>
        ) : null}

        {/* Features Banner */}
        

        {/* Get Started Section */}
        <GetStarted />
        <FeaturesBanner />
      </div>

    
    </div>
  );
}
